// src/pages/portal/Page_therapist/Page_Therapist_Salas/Page_Therapist_Salas.js
import React, { useMemo, useState } from "react";
import {
  Layout,
  Spin,
  ConfigProvider,
  Empty,
  Typography,
  Divider,
  Button,
  Drawer,
  Badge,
  Input,
} from "antd";
import {
  StarFilled,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { reviTheme } from "../../../../theme/reviTheme";

// Controller
import { useSalasController } from "./useSalasController";

// Componentes UI
import {
  TherapyCat,
  TherapyWizard,
  SceneDetailModal,
  TherapyDetailModal,
  AddSceneToTherapyWizard,
} from "../../../../components/Admin/SalasCmp";
import { SharedTherapiesModal } from "../../../../components/Admin/SalasCmp/SharedTherapiesModal";
import {
  SceneCard,
  TherapySidebar,
} from "../../../../components/Admin/uiComponents";

import { useShellEstrecho } from "../../../../hooks/useIsNarrow";

import "./Page_Therapist_Salas.scss";

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

export function Page_Therapist_Salas() {
  const { t } = useTranslation("therapist");
  const { t: tUi } = useTranslation("ui");
  const shellEstrecho = useShellEstrecho();
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const {
    loading,
    categories,
    scenes,
    myTherapies,
    cargarMasTerapias,
    cargandoMasTerapias,
    totalTerapias,
    quedanTerapias,
    selectedCat,
    setSelectedCat,

    modalState,
    openModal,
    closeModal,

    wizardState,
    openWizard,
    closeWizard,
    handleTherapyCreated,
    handleEditTherapy,

    addSceneWizard,
    openAddSceneWizard,
    closeAddSceneWizard,
    handleAddSceneSuccess,

    currentTherapyDetail,
    isDetailModalOpen,
    detailLoading,
    handleViewTherapy,
    closeDetailModal,
    handleRemoveStep,

    // ✅ NUEVO: viene del controller/hook de detalle
    handleMoveStepUp,

    handleDeleteTherapy,

    sharedOpen,
    sharedLoading,
    sharedTherapies,
    sharedPage,
    sharedPageSize,
    sharedTotal,
    openSharedModal,
    closeSharedModal,
    handleSharedSearch,
    handleSharedPageChange,
    handleCloneSharedTherapy,

    launchScene,
  } = useSalasController();

  /**
   * El catalogo llega a las 185 salas. Sin buscador, encontrar una concreta en
   * un movil son mas de cuarenta pantallas de arrastre, y el filtro por
   * categoria no ayuda cuando ya sabes el nombre de la sala que quieres.
   *
   * Filtra sobre lo que ya esta en memoria: no hay peticion nueva ni cambia
   * nada de lo que el servidor devuelve.
   */
  const escenasVisibles = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino || !scenes) return scenes;
    return scenes.filter((s) =>
      String(s?.module_01?.ModOneTitle || "")
        .toLowerCase()
        .includes(termino),
    );
  }, [scenes, busqueda]);

  const { premiumScenes, standardScenes } = useMemo(() => {
    const scenes = escenasVisibles;
    if (!scenes) return { premiumScenes: [], standardScenes: [] };

    const premium = [];
    const standard = [];

    scenes.forEach((scene) => {
      if (scene.module_07?.ReviScenePremium === true) {
        premium.push(scene);
      } else {
        standard.push(scene);
      }
    });

    return { premiumScenes: premium, standardScenes: standard };
  }, [escenasVisibles]);

  return (
    <ConfigProvider theme={reviTheme}>
      <Layout className="revi-page-salas">
        <Content className="revi-page-salas__main">
          <header className="revi-page-salas__header">
            <div>
              <Title level={2} style={{ marginBottom: 0, color: "#111827" }}>
                {t("salas.titulo")}
              </Title>
              <Text type="secondary" className="revi-subtitulo-pagina">
                {t("salas.subtitulo")}
              </Text>
            </div>

            {/* En estrecho, el unico acceso al panel de terapias. El contador
                dice cuantas hay sin tener que abrirlo. */}
            {shellEstrecho && (
              <Badge count={totalTerapias || 0} offset={[-4, 4]}>
                <Button
                  className="revi-page-salas__panelBtn"
                  icon={<UnorderedListOutlined />}
                  onClick={() => setPanelAbierto(true)}
                  aria-label={tUi("terapias.misTerapias", {
                    count: totalTerapias || 0,
                  })}
                />
              </Badge>
            )}
          </header>

          {/* En los dos anchos: con 185 salas, el filtro por categoria no
              sirve cuando ya sabes el nombre de la que buscas. */}
          <Input
            allowClear
            className="revi-page-salas__buscador"
            prefix={<SearchOutlined />}
            placeholder={t("salas.buscarPlaceholder")}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />

          <div className="revi-page-salas__filters">
            <TherapyCat
              categories={categories}
              selected={selectedCat}
              onSelect={setSelectedCat}
            />
          </div>

          <div className="revi-page-salas__grid-wrapper">
            {loading ? (
              <div className="loader-container">
                <Spin size="large" />
              </div>
            ) : escenasVisibles?.length > 0 ? (
              <>
                {premiumScenes.length > 0 && (
                  <div className="scenes-section premium-section">
                    <div className="section-header">
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#d97706",
                        }}
                      >
                        <StarFilled /> {t("salas.destacadas")}
                      </Title>
                    </div>

                    <div className="scenes-grid">
                      {premiumScenes.map((scene) => (
                        <SceneCard
                          key={scene._id}
                          scene={scene}
                          onClick={launchScene}
                          onInfo={openModal}
                          onAdd={(s) => openAddSceneWizard(s)}
                          isPremium={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {premiumScenes.length > 0 && standardScenes.length > 0 && (
                  <Divider style={{ margin: "32px 0" }} />
                )}

                {standardScenes.length > 0 && (
                  <div className="scenes-section standard-section">
                    {premiumScenes.length > 0 && (
                      <div className="section-header">
                        <Title
                          level={5}
                          type="secondary"
                          style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <AppstoreOutlined /> {t("salas.catalogoGeneral")}
                        </Title>
                      </div>
                    )}

                    <div className="scenes-grid">
                      {standardScenes.map((scene) => (
                        <SceneCard
                          key={scene._id}
                          scene={scene}
                          onClick={launchScene}
                          onInfo={openModal}
                          onAdd={(s) => openAddSceneWizard(s)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-container">
                <Empty description={t("salas.vacio")} />
              </div>
            )}
          </div>
        </Content>

        {/*
          El panel de terapias es un SEGUNDO panel lateral dentro del que ya
          pone el shell. A 375 px sumaban 580 px de cromo sobre un viewport de
          375, y esta es la pantalla a la que aterriza el terapeuta. Por debajo
          de 992 no se renderiza: el mismo panel se monta en un cajon propio que
          abre el boton de la cabecera.
        */}
        {!shellEstrecho && (
          <Sider width={320} theme="light" className="revi-page-salas__sider">
            <TherapySidebar
              therapies={myTherapies}
              onCreate={openWizard}
              onShared={openSharedModal}
              onView={handleViewTherapy}
              onEdit={handleEditTherapy}
              onDelete={handleDeleteTherapy}
              onCargarMas={cargarMasTerapias}
              cargandoMas={cargandoMasTerapias}
              quedan={quedanTerapias}
              total={totalTerapias}
            />
          </Sider>
        )}

        {shellEstrecho && (
          <Drawer
            placement="right"
            open={panelAbierto}
            onClose={() => setPanelAbierto(false)}
            size={320}
            title={tUi("terapias.misTerapias", {
              count: totalTerapias || 0,
            })}
            rootClassName="revi-page-salas__drawer"
            styles={{ body: { padding: 12 } }}
          >
            <TherapySidebar
              therapies={myTherapies}
              onCreate={openWizard}
              onShared={openSharedModal}
              onView={handleViewTherapy}
              onEdit={handleEditTherapy}
              onDelete={handleDeleteTherapy}
              onCargarMas={cargarMasTerapias}
              cargandoMas={cargandoMasTerapias}
              quedan={quedanTerapias}
              total={totalTerapias}
            />
          </Drawer>
        )}

        <SceneDetailModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          scene={modalState.scene}
          onLaunch={launchScene}
        />

        {wizardState.open && (
          <TherapyWizard
            key={
              wizardState.data ? `edit-${wizardState.data._id}` : "create-new"
            }
            isOpen={true}
            onClose={closeWizard}
            onSuccess={handleTherapyCreated}
            initialData={wizardState.data}
          />
        )}

        {addSceneWizard.open && (
          <AddSceneToTherapyWizard
            key={addSceneWizard.scene?._id || "add-scene"}
            isOpen={true}
            onClose={closeAddSceneWizard}
            scene={addSceneWizard.scene}
            onSuccess={handleAddSceneSuccess}
          />
        )}

        <TherapyDetailModal
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          therapy={currentTherapyDetail}
          loading={detailLoading}
          onRemoveStep={handleRemoveStep}
          onViewScene={openModal}
          onMoveStepUp={handleMoveStepUp}
        />

        <SharedTherapiesModal
          isOpen={sharedOpen}
          onClose={closeSharedModal}
          loading={sharedLoading}
          therapies={sharedTherapies}
          page={sharedPage}
          pageSize={sharedPageSize}
          total={sharedTotal}
          onPageChange={handleSharedPageChange}
          onSearch={handleSharedSearch}
          onView={handleViewTherapy}
          onClone={handleCloneSharedTherapy}
        />
      </Layout>
    </ConfigProvider>
  );
}
